import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  ContentCardWithInput,
  InputText,
  InputTextarea,
} from '@teo/components';
import {
  Training,
  Evaluation,
  Flag,
  Plus,
  CrossFilled,
} from '@teo/components/icons';
import { useGet } from '@/query/fetchHooks';
import { nthLastIndexOf, isEmpty } from '@/util';
import { axios } from '@/axios';
import { config } from '@/config';
import ActionLP from './components/ActionLP';
import SettingsLP from './components/SettingsLP';
import ResultsLP from './components/ResultsLP';
import PracticalInformationLP from './components/PracticalInformationLP';
import TraningLink from './components/TraningLink';
import SettingsMilestone from './components/SettingsMilestone';
import { useMediaQuery } from 'react-responsive';
import AddCompetenceLP from '@/components/Competencies/AddCompetenceLP';

const TrainingSettings = ({
  learningpathId,
  authOrg,
  nodeSelected,
  handleChangeNode,
  handleRemoveNode,
  handleMakeTheme,
  handleRemoveChildNode,
  setNodes,
  nodes,
  setEdges,
  edges,
  authUser,
}) => {
  const { t } = useTranslation();
  const [title, setTitle] = useState(nodeSelected?.data?.title);
  const [description, setDescription] = useState(
    nodeSelected?.data?.description || ''
  );
  const [trainingDetail, setTrainingDetail] = useState(null);
  const [trainingCompetencies, setTrainingCompetencies] = useState([]);
  const [newTrainingCompetencies, setNewTrainingCompetencies] = useState([]);
  const [isOpenCompetenciesModal, setIsOpenCompetenciesModal] = useState();

  const [typeOfTraining, setTypeOfTraining] = useState();

  // const [evaluationType, setEvaluationType] = useState('internal');

  const [trainingContentLinking, setTrainingContentLinking] = useState('');

  const examplePath = window.location.pathname.slice(
    0,
    nthLastIndexOf(window.location.pathname, '/', 2)
  );

  const basePath = window.location.pathname;

  const isMobile = useMediaQuery({
    query: '(max-width: 767px)',
  });

  useGet(`/training/${nodeSelected?.data?.nodeID}`, {
    retry: false,
    enabled: !!nodeSelected?.data?.trainingId,
    onSuccess: (result) => {
      setTrainingDetail(result?.data || {});
      setDescription(result?.data?.trainingDescription || '');
      setTypeOfTraining(result?.data?.typeOfTraining);
      setTrainingContentLinking(result?.data?.contentLinkingType);
      setNodes((nds) =>
        nds?.map((node) =>
          node.id === nodeSelected.id
            ? {
                ...node,
                data: {
                  ...node.data,
                  coursecontentId: result?.data?.coursecontentId,
                  publicationCourseType: result?.data?.publicationCourseType,
                },
              }
            : node
        )
      );
    },
  });

  const { refetch: refetchCompetencies } = useGet(
    `/trainingcompetencedetails`,
    {
      trainingId: nodeSelected?.data?.nodeID,
      onSuccess: (result) => {
        setTrainingCompetencies(result?.data);
        setNewTrainingCompetencies(result?.data);
      },
    }
  );

  const habdleChangeTitle = (value) => {
    setTitle(value);
    if (learningpathId === nodeSelected?.data?.learningPathId) {
      setNodes(
        nodes?.map((node) =>
          node.id === nodeSelected.id
            ? { ...node, data: { ...node.data, title: value } }
            : node
        )
      );
    } else {
      setNodes(
        nodes?.map((node) =>
          node.data?.nodeID === nodeSelected?.data?.learningPathId
            ? {
                ...node,
                data: {
                  ...node?.data,
                  child: node?.data?.child?.map((el) =>
                    el?.id === nodeSelected.id ? { ...el, name: value } : el
                  ),
                },
              }
            : node
        )
      );
    }
  };

  const habdleChangeDescription = (value) => {
    setDescription(value);
    setNodes(
      nodes?.map((node) =>
        node.id === nodeSelected.id
          ? { ...node, data: { ...node.data, description: value } }
          : node
      )
    );
  };

  const save = async () => {
    const changes = {};
    if (title !== trainingDetail?.trainingName) {
      changes.trainingName = title;
    }
    if (description !== trainingDetail?.trainingDescription) {
      changes.trainingDescription = description;
    }
    const promises = [];
    if (!isEmpty(changes)) {
      promises.push(
        axios.put(
          `${config.backend}/training/${nodeSelected?.data?.nodeID}`,
          changes
        )
      );
    }
  };

  const handleChangeTraining = async (change) => {
    if (trainingDetail) {
      const result = await axios.put(
        `${config.backend}/training/${trainingDetail?.id}`,
        change
      );
      setTrainingDetail((prev) => result?.data || prev);
    }
  };

  const hendleDeleteComp = async (item) => {
    try {
      await axios.delete(`${config.backend}/trainingcompetence/${item.id}`);
      const listCompetencies = newTrainingCompetencies?.filter(
        (el) => el?.id !== item.id
      );
      setTrainingCompetencies(listCompetencies);
      setNewTrainingCompetencies(listCompetencies);
    } catch (error) {
      console.error('Error saving:', error);
    }
  };

  return (
    <div
      className={`flex h-full max-h-[calc(100vh_-_150px)] flex-col overflow-hidden ${
        isMobile && 'min-h-[calc(100vh_-_126px)]'
      }`}
    >
      <div className="flex h-full max-h-[calc(100vh_-_200px)] flex-col overflow-y-auto">
        <ContentCardWithInput
          icon={
            trainingDetail?.trainingType === 'milestone'
              ? Flag
              : trainingDetail?.trainingType === 'evaluation'
              ? Evaluation
              : Training
          }
          color={
            trainingDetail?.trainingType === 'milestone'
              ? 'blue'
              : trainingDetail?.trainingType === 'evaluation'
              ? 'purple'
              : 'green'
          }
        >
          <div className="pt-4">
            <InputText
              value={title}
              onChange={(e) => habdleChangeTitle(e.currentTarget.value)}
              className="mb-2"
              onBlur={save}
            />
            <InputTextarea
              placeholder={
                trainingDetail?.trainingType === 'training'
                  ? t('pages.learning_path.settings.tr_description')
                  : trainingDetail?.trainingType === 'evaluation'
                  ? t('pages.learning_path.settings.ev_description')
                  : t('pages.learning_path.settings.mil_description')
              }
              onChange={(event) => habdleChangeDescription(event.target.value)}
              value={description}
              // limit={300}
              minRows={1}
              customStyles="bg-white inputWhiteBg  text-sm px-[6px]"
              onBlur={save}
            />
          </div>
        </ContentCardWithInput>
        {trainingDetail?.trainingType === 'milestone' && (
          <SettingsMilestone
            trainingDetail={trainingDetail}
            handleChangeTraining={handleChangeTraining}
            setTypeOfTraining={setTypeOfTraining}
            typeOfTraining={typeOfTraining}
            title={title}
            authUser={authUser}
          />
        )}
        {(trainingDetail?.trainingType === 'training' ||
          trainingDetail?.trainingType === 'evaluation') && (
          <>
            {nodeSelected?.data?.learningPathId == learningpathId && (
              <ActionLP
                nodeSelected={nodeSelected}
                edges={edges}
                nodes={nodes}
                setNodes={setNodes}
                handleMakeTheme={handleMakeTheme}
              />
            )}
            <SettingsLP
              learningpathId={learningpathId}
              nodeSelected={nodeSelected}
              trainingDetail={trainingDetail}
              handleChangeNode={handleChangeNode}
              handleChangeTraining={handleChangeTraining}
              edges={edges}
              setEdges={setEdges}
              nodes={nodes}
              setNodes={setNodes}
              setTypeOfTraining={setTypeOfTraining}
              typeOfTraining={typeOfTraining}
              setTrainingContentLinking={setTrainingContentLinking}
              trainingContentLinking={trainingContentLinking}
            />
          </>
        )}
        {((trainingDetail?.trainingType === 'training' &&
          (typeOfTraining !== 'online' || trainingContentLinking === 'URL')) ||
          (trainingDetail?.trainingType === 'evaluation' &&
            typeOfTraining === 'external')) && (
          <>
            <ResultsLP
              trainingDetail={trainingDetail}
              handleChangeTraining={handleChangeTraining}
            />
            <PracticalInformationLP
              authOrg={authOrg}
              trainingDetail={trainingDetail}
              typeOfTraining={typeOfTraining}
              handleChangeTraining={handleChangeTraining}
            />
          </>
        )}

        {trainingDetail?.trainingType === 'training' &&
          typeOfTraining === 'online' &&
          trainingContentLinking !== 'URL' && (
            <TraningLink
              trainingDetail={trainingDetail}
              trainingContentLinking={trainingContentLinking}
              handleChangeTraining={handleChangeTraining}
            />
          )}
        <div className=" mb-6 border-t px-5 pt-5">
          <h4 className="mb-4 text-base">{t('links.competencies')}</h4>

          {trainingCompetencies?.length > 0 && (
            <div className="mb-2 flex flex-wrap gap-2">
              {trainingCompetencies?.map((comp) => {
                return (
                  <div
                    key={comp?.id}
                    className="flex rounded-md border bg-grey-01"
                  >
                    <div className="flex items-center gap-2 px-3 py-1 text-sm leading-none text-grey-08">
                      {comp?.qualificationCategoryColorCode && (
                        <span
                          style={{
                            backgroundColor:
                              comp?.qualificationCategoryColorCode,
                          }}
                          className={`h-1.5 w-1.5 min-w-1.5 rounded-full`}
                        ></span>
                      )}
                      <p className="leading-[1.3]">{comp.name}</p>

                      <div>
                        <Button
                          className="!px-1 !py-1"
                          variant="ghost"
                          onClick={() => {
                            hendleDeleteComp(comp);
                          }}
                        >
                          <CrossFilled className="h-4 w-4 text-grey-06" />
                        </Button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
          {trainingCompetencies?.length > 0 ? (
            <Button
              onClick={() => setIsOpenCompetenciesModal(true)}
              variant="outline"
            >
              {t('pages.manage_competencies.add_competence')}
            </Button>
          ) : (
            <Button
              variant="ghost"
              className="flex w-full items-center gap-2 border !border-grey-02 !p-4 !text-[#1E1E2080]"
              onClick={() => {
                setIsOpenCompetenciesModal(true);
              }}
            >
              {t('pages.learning_path.no_competencies_added')}
              <Plus className="ml-auto w-5 text-[#4D4D50]" />
            </Button>
          )}

          {isOpenCompetenciesModal && (
            <AddCompetenceLP
              setIsOpenCompetencies={setIsOpenCompetenciesModal}
              isOpenCompetencies={isOpenCompetenciesModal}
              node={nodeSelected?.data}
              setNewCompetencies={setNewTrainingCompetencies}
              newCompetencies={newTrainingCompetencies}
              competencies={trainingCompetencies}
              refetchCompetencies={refetchCompetencies}
              training={true}
            />
          )}
        </div>
      </div>
      <div className="mt-auto flex flex-col gap-2 border-t px-5 py-5 shadow-[0px_7px_30px_-9px_grey]">
        <Button
          variant="outline"
          className=""
          onClick={() => {
            window.open(
              `${basePath}/${nodeSelected?.data?.courseType}/${nodeSelected?.data?.trainingId}/preview`,
              '_blank'
            );
          }}
        >
          {nodeSelected?.data?.courseType === 'training' &&
            t('pages.learning_path.view_tr')}
          {nodeSelected?.data?.courseType === 'evaluation' &&
            t('pages.learning_path.view_ev')}
          {nodeSelected?.data?.courseType === 'milestone' &&
            t('pages.learning_path.view_mil')}
        </Button>
        <Button
          variant="ghost"
          className="!text-error-04"
          onClick={() =>
            nodeSelected?.data?.learningPathId == learningpathId
              ? handleRemoveNode(nodeSelected)
              : handleRemoveChildNode(nodeSelected, nodes)
          }
        >
          {nodeSelected?.data?.courseType === 'training' &&
            t('pages.learning_path.delete_tr')}
          {nodeSelected?.data?.courseType === 'evaluation' &&
            t('pages.learning_path.delete_ev')}
          {nodeSelected?.data?.courseType === 'milestone' &&
            t('pages.learning_path.delete_mil')}
        </Button>
      </div>
    </div>
  );
};

export default TrainingSettings;
